import { CrmApiError, crmRequest } from '../http'
import { POLL_DELAYS_MS, POLL_STEADY_MS, PROCESS_TIMEOUT_MS } from './constants'
import type {
  ConfirmBody,
  ConfirmResponse,
  CreateCaptureBody,
  MentionChoiceBody,
  PatchCaptureBody,
  VoiceCapture,
  VoiceStatus,
} from './types'

const PATH = '/voice-captures'

const json = (method: string, body?: unknown): RequestInit => ({
  method,
  ...(body === undefined ? {} : { body: JSON.stringify(body) }),
})

export function getStatus(): Promise<VoiceStatus> {
  return crmRequest<VoiceStatus>(`${PATH}/status`, undefined)
}

export function listOpenCaptures(): Promise<VoiceCapture[]> {
  return crmRequest<VoiceCapture[]>(`${PATH}?open=true`, undefined)
}

export function createCapture(body: CreateCaptureBody): Promise<VoiceCapture> {
  return crmRequest<VoiceCapture>(PATH, json('POST', body))
}

export function getCapture(id: string): Promise<VoiceCapture> {
  return crmRequest<VoiceCapture>(`${PATH}/${id}`, undefined)
}

export function patchCapture(id: string, body: PatchCaptureBody): Promise<VoiceCapture> {
  return crmRequest<VoiceCapture>(`${PATH}/${id}`, json('PATCH', body))
}

export function deleteCapture(id: string): Promise<void> {
  return crmRequest<void>(`${PATH}/${id}`, json('DELETE'))
}

/** One synchronous process call; the server answers `klaar` or `mislukt`. */
export function processCapture(id: string, signal?: AbortSignal): Promise<VoiceCapture> {
  return crmRequest<VoiceCapture>(`${PATH}/${id}/process`, { ...json('POST'), signal })
}

export function chooseMention(id: string, ref: string, choice: MentionChoiceBody): Promise<VoiceCapture> {
  return crmRequest<VoiceCapture>(`${PATH}/${id}/mentions/${encodeURIComponent(ref)}`, json('POST', choice))
}

export function confirmCapture(id: string, body: ConfirmBody): Promise<ConfirmResponse> {
  return crmRequest<ConfirmResponse>(`${PATH}/${id}/confirm`, json('POST', body))
}

export function undoCapture(id: string): Promise<VoiceCapture> {
  return crmRequest<VoiceCapture>(`${PATH}/${id}/undo`, json('POST'))
}

function abortError(): DOMException {
  return new DOMException('Afgebroken', 'AbortError')
}

function pause(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(abortError())
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      clearTimeout(timer)
      reject(abortError())
    }
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

/** Errors after which the memo may still be processing on the server: look it up instead. */
function mayBeRunning(caught: unknown): boolean {
  if (caught instanceof DOMException && caught.name === 'AbortError') return true
  if (caught instanceof CrmApiError) return caught.code === 'network' || caught.code === 'processing_active'
  return false
}

/**
 * Processes a capture. The call aborts after 45 seconds (or when the connection drops, or
 * the server says it is already busy) and the result is polled with a 1, 2, 4, 8 s backoff,
 * then every 10 s, until the capture leaves `verwerken`. Resolves to the finished capture;
 * rejects for any other error, and with an AbortError when the caller's signal fires.
 */
export async function processAndWait(
  id: string,
  options: { signal?: AbortSignal } = {},
): Promise<VoiceCapture> {
  const { signal } = options
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), PROCESS_TIMEOUT_MS)
  const forward = () => controller.abort()
  signal?.addEventListener('abort', forward, { once: true })
  try {
    return await processCapture(id, controller.signal)
  } catch (caught) {
    if (signal?.aborted) throw abortError()
    if (!mayBeRunning(caught)) throw caught
  } finally {
    clearTimeout(timer)
    signal?.removeEventListener('abort', forward)
  }

  for (let attempt = 0; ; attempt++) {
    await pause(POLL_DELAYS_MS[attempt] ?? POLL_STEADY_MS, signal)
    try {
      const capture = await getCapture(id)
      if (capture.status !== 'verwerken') return capture
    } catch (caught) {
      if (!mayBeRunning(caught) && !(caught instanceof CrmApiError && caught.status >= 500)) throw caught
    }
  }
}
